import {useContext, useEffect, useState} from 'react';

import {ButtonSize, Color} from '../../../../types';
import {Button} from '../../../../components';
import ChangeGeneralLeaderboardVisibility from './settings/ChangeGeneralLeaderboardVisibility';
import {request, URLs} from '../../../../api';
import AppContext from '../../../../context/AppContext';
import ChangeModuleListedSettings from './settings/ChangeModuleListedSettings';
import {ModuleGeneralLeaderboardVisibility, ModuleSettings} from '../../../../types/entities/ModuleType';
import ManageModulePicture from './settings/ManageModulePicture';

function ModuleAdminGeneralSettingsPage() {
  const {user, dispatchError} = useContext(AppContext);

  const [oldModuleSettings, setOldModuleSettings] = useState<ModuleSettings | null>(null);
  const [newModuleSettings, setNewModuleSettings] = useState<ModuleSettings | null>(null);

  const [submitButtonEnabled, setSubmitButtonEnabled] = useState(false);

  useEffect(() => {
    if (user?.activeModule) {
      request<never, never, ModuleSettings>(URLs.getModuleSettings(user?.activeModule?.moduleId), {
        method: 'GET',
        successCallback(response) {
          setOldModuleSettings(response);
          setNewModuleSettings(response);
        },
        errorCallback: (e: any) => {
          dispatchError({
            errorMessage: e.response.data.error ?? 'There was an error. Please try again later.',
          });
        },
      });
    }
  }, []);

  useEffect(() => {
    if (!areModuleSettingsEqual(oldModuleSettings, newModuleSettings)) {
      setSubmitButtonEnabled(true);
    } else {
      setSubmitButtonEnabled(false);
    }
  }, [oldModuleSettings, newModuleSettings]);

  const areModuleSettingsEqual = (oldSettings: ModuleSettings | null, newSettings: ModuleSettings | null) => {
    if (oldSettings === newSettings) return true;
    if (!oldSettings || !newSettings) return false;

    return JSON.stringify(oldSettings) === JSON.stringify(newSettings);
  };

  const handleChangeLeaderboardVisibility = (newVisibility: ModuleGeneralLeaderboardVisibility) => {
    setNewModuleSettings((prev) => (prev ? {...prev, generalLeaderboardVisibility: newVisibility} : prev));
  };

  const handleChangeModuleListedSettings = (newVisibility: boolean) => {
    setNewModuleSettings((prev) => (prev ? {...prev, isPubliclyListed: newVisibility} : prev));
  };

  const handleSettingsUpdate = (newSettings: ModuleSettings | null) => {
    if (!newSettings) return;
    if (user?.activeModule) {
      request<never, {newSettings: Omit<ModuleSettings, 'pictureUUID'>}, never>(
        URLs.updateModuleSettings(user?.activeModule?.moduleId),
        {
          method: 'PUT',
          body: {
            newSettings: {
              isPubliclyListed: newSettings.isPubliclyListed,
              generalLeaderboardVisibility: newSettings.generalLeaderboardVisibility,
            },
          },
          successCallback() {
            setOldModuleSettings(newSettings);
            setSubmitButtonEnabled(false);
          },
          errorCallback: (e: any) => {
            dispatchError({
              errorMessage: e.response.data.error ?? 'There was an error. Please try again later.',
            });
          },
        }
      );
    }
  };

  return (
    <div className="px-4 h-fit overflow-y-scroll">
      {oldModuleSettings && (
        <>
          <div className="flex flex-col gap-1 mb-5">
            <h1 className="font-bold text-4xl">General Module Settings</h1>
            <p className="text-lg">
              Adjust the module settings to modify its functionality to meet your academic needs and requirements.
            </p>
          </div>
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-4 border p-4 rounded-lg shadow-sm border-gray-400">
              <h2 className="font-bold text-3xl">General Settings</h2>
              {oldModuleSettings && newModuleSettings && (
                <div className="flex flex-col gap-6">
                  <ChangeGeneralLeaderboardVisibility
                    selectedValue={newModuleSettings.generalLeaderboardVisibility}
                    handleChange={handleChangeLeaderboardVisibility}
                  />
                  <ChangeModuleListedSettings
                    selectedValue={newModuleSettings.isPubliclyListed}
                    handleChange={handleChangeModuleListedSettings}
                  />
                </div>
              )}
              <Button
                color={Color.PRIMARY}
                fullWidth={true}
                label={'Save changes'}
                size={ButtonSize.MEDIUM}
                type="submit"
                onClick={() => submitButtonEnabled && handleSettingsUpdate(newModuleSettings)}
                disabled={!submitButtonEnabled}
              />
            </div>

            <div className="border p-4 rounded-lg shadow-sm border-gray-400">
              <ManageModulePicture currentImageUUID={oldModuleSettings?.pictureUUID ?? null} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ModuleAdminGeneralSettingsPage;
